import { updateQuizProgress } from '@/data/services/activityElement/quizProgressServices';
import {
  createActivityProgressAttempt,
  getActivityProgressAttempt,
} from '@/data/services/activityProgressAttemptServices';
import useAuth from '@/data/hook/useAuth';
import { isStudent } from '@/functions/auth';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import ActivityProgress, {
  ActivityProgressAttempt,
  AttemptContent,
  IQuestionProgress,
  QuizActivityAttempt,
} from '@/models/_deprecated/ActivityProgress';
import {
  AnswerCheckResult,
  QuizProgress,
} from '@/utils/editorjs/activity-block';
import { AlternativeRaw } from '@/utils/editorjs/activity-block/model/alternative';
import { QuizRaw } from '@/utils/editorjs/activity-block/model/quiz';
import { useActivityTool } from './useActivityTool';

type QuestionProgressItem = NonNullable<
  ActivityProgress['questionsProgress']
>[number];

const isQuizQuestionProgress = (
  progress: QuestionProgressItem,
  quiz: QuizRaw,
): progress is IQuestionProgress =>
  'questionId' in progress &&
  progress.questionId === quiz.id &&
  progress.type === ActivityTypeEnum.QUIZ;

const isQuizAttemptContent = (
  content: AttemptContent,
  quiz: QuizRaw,
): content is AttemptContent & QuizActivityAttempt =>
  content.type === ActivityTypeEnum.QUIZ &&
  'data' in content &&
  content.data.quiz.id === quiz.id;

const findQuizProgress = (activityProgress: ActivityProgress, quiz: QuizRaw) =>
  activityProgress.questionsProgress?.find(progress =>
    isQuizQuestionProgress(progress, quiz),
  ) as IQuestionProgress | undefined;

const findQuizAttemptContent = (
  attempt: ActivityProgressAttempt,
  quiz: QuizRaw,
) =>
  attempt.content?.find(content => isQuizAttemptContent(content, quiz)) as
    | (AttemptContent & QuizActivityAttempt)
    | undefined;

export const useActivityToolWithProgress = (
  getActivityProgress: (quiz: QuizRaw) => Promise<ActivityProgress | undefined>,
  onChange?: () => Promise<void>,
) => {
  const { user } = useAuth();
  const studentView = !!user && isStudent(user.userType);

  const onCheckAnswer = async (
    answer: AlternativeRaw,
    quiz: QuizRaw,
  ): Promise<AnswerCheckResult | undefined> => {
    const activityProgress = await getActivityProgress(quiz);
    const quizProgress =
      activityProgress && findQuizProgress(activityProgress, quiz);

    if (!activityProgress || !quizProgress) {
      return studentView ? { status: 'unavailable' } : undefined;
    }

    await updateQuizProgress(
      {
        quizId: quiz.id,
        quizProgressId: quizProgress.progressId,
      },
      {
        answer: answer.verifier,
        isDone: true,
      },
    );

    const attempt = await createActivityProgressAttempt(activityProgress.id);
    void onChange?.();

    const attemptContent = findQuizAttemptContent(attempt, quiz);
    if (!attemptContent) return { status: 'unavailable' };

    return { status: 'checked', isCorrect: attemptContent.answers.isCorrect };
  };

  const getProgress = async (
    quiz: QuizRaw,
  ): Promise<QuizProgress | undefined> => {
    const activityProgress = await getActivityProgress(quiz);

    const bestAttempt = activityProgress?.bestAttempt;

    if (!activityProgress || !bestAttempt) return undefined;

    const attempt = await getActivityProgressAttempt({
      activityProgressId: activityProgress?.id,
      attemptId: bestAttempt.id,
    });

    const attemptContent = findQuizAttemptContent(attempt, quiz);

    if (!attemptContent) return undefined;

    return {
      answer: attemptContent.answers.answer ?? '',
      isCorrect: attemptContent.answers.isCorrect,
      isDone: attemptContent.answers.isDone,
    };
  };

  return useActivityTool({
    getProgress,
    onCheckAnswer,
    randomize: true,
  });
};
